import styled from "styled-components";
import { TrendingUp } from "lucide-react";
import { useSelector } from "react-redux";

const H1 = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 1.5rem;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const Pill = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: hsl(${({ theme }) => theme.colors.accent} / 0.1);
  color: hsl(${({ theme }) => theme.colors.accent});
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  font-weight: 700;
`;

const Table = styled.div`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid hsl(${({ theme }) => theme.colors.border});
  align-items: center;
  &:last-child {
    border-bottom: none;
  }
  @media (min-width: 640px) {
    grid-template-columns: auto 1fr auto auto auto;
    gap: 1rem;
  }
  &:hover {
    background: hsl(${({ theme }) => theme.colors.muted} / 0.3);
  }
`;

const statusStyle = (status) => {
  if (status === "used") return { bg: "hsl(221 67% 44% / 0.1)", fg: "hsl(221 67% 44%)" };
  if (status === "active") return { bg: "hsl(43 80% 52% / 0.1)", fg: "hsl(43 80% 45%)" };
  return { bg: "hsl(220 15% 94%)", fg: "hsl(220 10% 46%)" };
};

const Empty = styled.p`
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  font-size: 0.9375rem;
`;

export default function DashboardSavings() {
  const { isAuthenticated } = useSelector((s) => s.auth);
  const { savings } = useSelector((s) => s.user);

  const apiTotal = savings.reduce((sum, s) => sum + (Number(s.amount) || 0), 0);

  const rows = savings.map((s) => ({
    id: s.id,
    storeName: s.label || "Entry",
    storeLogo: "https://www.google.com/s2/favicons?domain=example.com&sz=64",
    title: s.note || s.label || "Recorded saving",
    discountValue: s.currency || "USD",
    savedAmount: `${s.currency || "USD"} ${Number(s.amount).toFixed(2)}`,
    status: "used",
  }));

  if (!isAuthenticated) {
    return (
      <div>
        <H1>My Savings</H1>
        <Empty>Sign in to see your savings.</Empty>
      </div>
    );
  }

  return (
    <div>
      <Head>
        <H1 style={{ margin: 0 }}>My Savings</H1>
        <Pill>
          <TrendingUp size={16} />$
          {Number(apiTotal).toFixed(2)}
          <span style={{ fontWeight: 400, fontSize: "0.875rem" }}>saved total</span>
        </Pill>
      </Head>
      {rows.length === 0 && (
        <Empty>No savings logged yet.</Empty>
      )}
      <Table>
        {rows.map((item) => {
          const st = statusStyle(item.status);
          return (
            <Row key={item.id}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img
                  src={item.storeLogo}
                  alt=""
                  style={{ width: 32, height: 32, borderRadius: 4, objectFit: "contain" }}
                />
                <span style={{ fontSize: "0.875rem", fontWeight: 500 }}>{item.storeName}</span>
              </div>
              <span style={{ fontSize: "0.875rem", overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.title}
              </span>
              <span style={{ fontWeight: 700, color: "hsl(221 67% 44%)" }}>{item.discountValue}</span>
              <span style={{ fontWeight: 600, color: "hsl(43 80% 52%)" }}>{item.savedAmount}</span>
              <span
                style={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  padding: "0.25rem 0.5rem",
                  borderRadius: 9999,
                  background: st.bg,
                  color: st.fg,
                  width: "fit-content",
                }}
              >
                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
              </span>
            </Row>
          );
        })}
      </Table>
    </div>
  );
}
